import { Address as ExtractedAddress, ExtractedAttribute } from '@common/types';
import { buildDynamicFormAddressValue } from '@common/utils';
import { useMemo } from 'react';
import { usePlacesWidget } from 'react-google-autocomplete';
import { useFormContext } from 'react-hook-form';
import { Stack } from '@common-components';
import { mapGoogleMapResultToStructuredAddress } from 'utils';
import FormAutocomplete from 'components/hookFormComponents/FormAutocomplete/FormAutocomplete';
import FormTextField from 'components/hookFormComponents/FormTextField';
import { FormStateBaseProp } from 'broker/pages/SubmissionWorkspacePage/components/DynamicForm/DynamicFormLayout/types';
import { allAddressQuestionIds } from 'broker/pages/SubmissionWorkspacePage/components/DynamicForm/DynamicFormLayout/utils/address-utils';
import { DynamicObjectFieldsComponentProps } from './DynamicObjectWrapper';
import { buildFieldName } from './utils/utils';

export default function DynamicFormAddress({
  dynamicQuestion,
  fieldNamePrefix,
  suggestion,
  onBlur,
  suggestionValidation,
}: DynamicObjectFieldsComponentProps) {
  const fields = dynamicQuestion.schema?.properties!;
  const { setValue, watch, trigger, getValues } = useFormContext();
  const watchValue = watch(fieldNamePrefix);
  const suggestionValue = suggestion as ExtractedAttribute<ExtractedAddress> | undefined;
  const { ref } = usePlacesWidget({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    options: {
      types: ['address'],
    },
    language: 'en',
    onPlaceSelected: async (place) => {
      const mappedAddress = mapGoogleMapResultToStructuredAddress(place);
      const mainValue = {
        ...watchValue.main,
        ...buildDynamicFormAddressValue(mappedAddress),
      };

      setValue(fieldNamePrefix, {
        ...watchValue,
        main: mainValue,
      });

      const formValues = getValues();

      allAddressQuestionIds.forEach((questionId) => {
        const isArrayField = Array.isArray(formValues[questionId]);
        const valueItem = isArrayField ? formValues[questionId][0]?.main?.line1 : formValues[questionId]?.main?.line1;
        if (!questionId.includes(fieldNamePrefix) && !valueItem) {
          setValue(`${questionId}${isArrayField ? '.0' : ''}.${FormStateBaseProp.Main}`, mainValue);
        }
      });
      await trigger();
      // send form after google auto-complete chosen
      onBlur();
    },
  });

  const schemaRequired = dynamicQuestion.schema?.required!;

  const ownBuildName = (fieldName: string) =>
    buildFieldName(
      `${fieldNamePrefix}.${FormStateBaseProp.Main}`,
      fieldName,
      `${fields[fieldName]!.title}`,
      dynamicQuestion,
    );

  const line1Suggestion = useMemo(
    () =>
      suggestionValue?.value?.line1 ? { value: suggestionValue.value.line1, reason: suggestion?.reason } : undefined,
    [suggestion?.reason, suggestionValue?.value?.line1],
  );

  const line2Suggestion = useMemo(
    () =>
      suggestionValue?.value?.line2 ? { value: suggestionValue.value.line2, reason: suggestion?.reason } : undefined,
    [suggestion?.reason, suggestionValue?.value?.line2],
  );

  const line3Suggestion = useMemo(
    () =>
      suggestionValue?.value?.line3 ? { value: suggestionValue.value.line3, reason: suggestion?.reason } : undefined,
    [suggestion?.reason, suggestionValue?.value?.line3],
  );

  const stateSuggestion = useMemo(
    () =>
      suggestionValue?.value?.state ? { value: suggestionValue.value.state, reason: suggestion?.reason } : undefined,
    [suggestion?.reason, suggestionValue?.value?.state],
  );

  const citySuggestion = useMemo(
    () =>
      suggestionValue?.value?.city ? { value: suggestionValue.value.city, reason: suggestion?.reason } : undefined,
    [suggestion?.reason, suggestionValue?.value?.city],
  );

  const postalSuggestion = useMemo(
    () =>
      suggestionValue?.value?.postal_code
        ? { value: suggestionValue.value.postal_code, reason: suggestion?.reason }
        : undefined,
    [suggestion?.reason, suggestionValue?.value?.postal_code],
  );

  const countryCodeSuggestion = useMemo(
    () =>
      suggestionValue?.value?.country_code
        ? { value: suggestionValue.value.country_code, reason: suggestion?.reason }
        : undefined,
    [suggestion?.reason, suggestionValue?.value?.country_code],
  );

  const organizationSuggestion = useMemo(
    () =>
      suggestionValue?.value?.organization
        ? { value: suggestionValue.value.organization, reason: suggestion?.reason }
        : undefined,
    [suggestion?.reason, suggestionValue?.value?.organization],
  );

  const hideSuggestion = suggestionValidation && !suggestionValidation?.showSuggestionValidation;

  return (
    <>
      <FormTextField
        // onBlur should not be called as it will be called by the google auto-complete (if added it will be called twice and add bugs like scrolling back to the top and not saving last scroll position)
        {...ownBuildName('line1')}
        inputProps={{
          ref,
        }}
        suggestion={line1Suggestion}
        enhancedRequired
        optional={!dynamicQuestion.schema?.required?.includes('line1')}
        suggestionValidation={suggestionValidation}
      />
      <FormTextField
        {...ownBuildName('line2')}
        suggestion={hideSuggestion ? undefined : line2Suggestion}
        enhancedRequired
        optional={!dynamicQuestion.schema?.required?.includes('line2')}
      />
      {schemaRequired.includes('line3') && (
        <FormTextField
          {...ownBuildName('line3')}
          onBlur={onBlur}
          suggestion={hideSuggestion ? undefined : line3Suggestion}
          enhancedRequired
          optional={!dynamicQuestion.schema?.required?.includes('line3')}
        />
      )}
      {schemaRequired.includes('organization') && (
        <FormTextField
          {...ownBuildName('organization')}
          suggestion={hideSuggestion ? undefined : organizationSuggestion}
          onBlur={onBlur}
          enhancedRequired
          optional={!dynamicQuestion.schema?.required?.includes('organization')}
        />
      )}
      <FormTextField
        {...ownBuildName('city')}
        onBlur={onBlur}
        suggestion={hideSuggestion ? undefined : citySuggestion}
        enhancedRequired
        optional={!dynamicQuestion.schema?.required?.includes('city')}
      />
      <Stack direction="row" gap={3}>
        <FormAutocomplete
          onBlur={onBlur}
          {...ownBuildName('state')}
          suggestion={hideSuggestion ? undefined : stateSuggestion}
          enhancedRequired
          optional={!dynamicQuestion.schema?.required?.includes('state')}
          options={fields.state.enum?.map((item) => ({ label: item, value: item })) || []}
        />
        <FormTextField
          onBlur={onBlur}
          {...ownBuildName('postal_code')}
          isNumberFormat
          numberFormatProps={{
            prefix: '',
            thousandSeparator: false,
            decimalScale: 0,
            allowLeadingZeros: true,
          }}
          suggestion={hideSuggestion ? undefined : postalSuggestion}
          enhancedRequired
          optional={!dynamicQuestion.schema?.required?.includes('postal_code')}
        />
      </Stack>

      <FormAutocomplete
        onBlur={onBlur}
        {...ownBuildName('country_code')}
        suggestion={hideSuggestion ? undefined : countryCodeSuggestion}
        enhancedRequired
        optional={!dynamicQuestion.schema?.required?.includes('country_code')}
        options={fields.country_code.enum?.map((item) => ({ label: item, value: item })) || []}
      />
    </>
  );
}
